import * as React from "react";
import { History } from "history";
import { Link } from "react-router-dom";

import { createIndicator } from "./Indicator";
import { createBulletGraph } from "./BulletGraph";
import Panel from "./Panel";
import Band from "./Band";
import Content from "./Content";
import Title from "./Title";
import Subtitle from "./Subtitle";
import Label from "./Label";
import type { ManufacturingData } from "./Dashboard";

import EventTable from "../EventTable";
import GantchartStandalone from "../manufacturing/GantchartStandalone";
import BatchOperations from "../manufacturing/BatchOperations";
import ShiftTableStandalone, { type DataDef } from "../manufacturing/ShiftTableStandalone";
import { toISODateTimeString, hashCode } from "../../lib/Util";
import EventListStandalone from "../EventListStandalone";
import LineLayout from "../manufacturing/dashboard/LineLayout";
import ToolSetup from "../manufacturing/dashboard/ToolSetup";
import type { DashboardData, DashboardLayout, BandType, PanelType } from "../../lib/Models";
import img_leap_logo from "../../img/leap-logo.svg";

type CreateShiftTableProps = {
    data_def: DataDef;
    data: DashboardData;
    widget: DashboardLayout;
    history: History;
};

const CreateShiftTable = (props: CreateShiftTableProps) => {
    const { data_def, widget, history, data } = props;

    const show_title = widget && widget.show_title;
    const show_line_gantt = widget && widget.show_line_gantt;

    // if data_def is undefined all lines will be displayed (no filtering)
    return (
        <ShiftTableStandalone
            history={history}
            show_title={show_title}
            data_def={data_def}
            show_line_gantt={show_line_gantt}
            data={data}
        />
    );
};

type CreateWidgetProps = {
    widget: DashboardLayout;
    data: DashboardData;
    history: History;
};

const CreateWidget = (props: CreateWidgetProps) => {
    const { widget, data, history } = props;

    if (widget.type === "iot" || widget.type === "timeseries" || widget.type === "sparkline") {
        // Deprecated widgets
        return <Label label="No data" size={widget.size} />;
    }
    if (widget.type === "indicator") {
        return createIndicator(data, widget);
    }
    if (widget.type === "bullet-graph") {
        return createBulletGraph(data, widget);
    }
    if (widget.type === "title") {
        return <Title label={widget.label} />;
    }
    if (widget.type === "subtitle") {
        return <Subtitle label={widget.label} />;
    }
    if (widget.type === "label") {
        return <Label label={widget.label} size={widget.size} />;
    }
    if (widget.type === "events") {
        if (data.events) {
            return <EventTable events={data.events} />;
        }
        return <div>No events</div>;
    }
    if (widget.type === "event-list") {
        return <EventListStandalone events={data.events} />;
    }
    if (widget.type === "manufacturing-gantt") {
        const data_def = data && data.external && data.external["manufacturing-gantt"];
        const hashKey = hashCode(JSON.stringify(data_def || ""));
        // if data_def is undefined entire gantt will be generated (no filtering)
        return (
            <GantchartStandalone key={hashKey} data_def={data_def} history={history} source_type={data.source_type} />
        );
    }
    if (widget.type === "manufacturing-batch-operations") {
        const data_def = data && data.external && data.external["manufacturing-batch-operations"];
        const batchop_group_uuid = data_def.batchop_group_uuid;
        return (
            <BatchOperations
                key={"manufacturing-batch-operations"}
                batchOpGroupUuid={batchop_group_uuid}
                readOnly={true}
                showHistoryLink={false}
                showHeader={false}
            />
        );
    }
    if (widget.type === "shift-table") {
        const data_def: DataDef = data && data.external && data.external["shift-table"];
        const hashKey = hashCode(JSON.stringify(data_def || "") + JSON.stringify(widget || ""));
        return <CreateShiftTable data_def={data_def} key={hashKey} widget={widget} history={history} data={data} />;
    }
    if (widget.type === "container") {
        const bands = widget.bands.map((x, i) => (
            <CreateBand band={x} data={data} ii={i} history={history} key={`band_${i}`} />
        ));
        return <Content panels={bands} />;
    }
    if (widget.type === "manufacturing-line-layout") {
        const hashKey: string = widget.uuid;
        const title: string = widget.title;
        const uuid: string = widget.uuid;
        const compact: boolean = widget.compact === true;
        const manufacturing: ManufacturingData = data && data.manufacturing;

        return <LineLayout key={hashKey} title={title} uuid={uuid} compact={compact} manufacturing={manufacturing} />;
    }
    if (widget.type === "manufacturing-tool-setup") {
        const data_def = data && data.external && data.external["manufacturing-tool-setup"];
        return <ToolSetup history={history} data_def={data_def} />;
    }
    throw new Error("Unknown widget type");
};

type CreatePanelProps = {
    panel: PanelType;
    data: DashboardData;
    i: number;
    history: History;
};
const CreatePanel = ({ panel, data, i, history }: CreatePanelProps) => {
    const widgets_def = panel.widgets || [];
    const widgets = widgets_def.map(x => <CreateWidget key={x.label} widget={x} data={data} history={history} />);
    return <Panel key={i} panels={widgets} colSpan={panel.col_span || 1} />;
};

type CreateBandProps = {
    band: BandType;
    data: DashboardData;
    ii: number;
    history: History;
};
const CreateBand = (props: CreateBandProps) => {
    const { band, data, ii, history } = props;

    const panels_def = band.panels || [];
    const panels = panels_def.map((x, i) => (
        <CreatePanel panel={x} data={data} i={i} history={history} key={`band_${ii}_${i}`} />
    ));

    return <Band key={ii} panels={panels} />;
};

type CreateClassicDashboardProps = {
    root: DashboardLayout;
    data: DashboardData;
    range_change: ((x: string) => void) | null;
    show_filter_bar: boolean;
    history: History;
};
export const CreateClassicDashboard = (props: CreateClassicDashboardProps) => {
    const { root, data, range_change, show_filter_bar, history } = props;

    if (data.external && data.external.refresh_time) {
        setTimeout(() => {
            window.location.reload();
        }, data.external.refresh_time * 1000);
    }

    const title = root.title;
    const ts_from = new Date(data.from);
    const ts_to = new Date(data.to);
    const bands = root.bands.map((x: BandType, i: number) => (
        <CreateBand band={x} data={data} ii={i} history={history} key={i} />
    ));

    let time_ranges: React.ReactNode[] = [];
    if (range_change) {
        time_ranges = ["6H", "1D", "3D", "1W", "2W", "1M", "3M", "1Y"].map(x => (
            <span className="time_range_item" tabIndex={0} key={x} onClick={() => range_change(x)}>
                {x}
            </span>
        ));
    }
    return (
        // class "ql-dash-finalized" is used to determine when the dashboard rendering has been finalized
        // this is important for png and pdf rendering of reports
        <div className="data_sources full-height ql-dash-finalized" id="statistics">
            {show_filter_bar && (
                <div className="filter_bar dashboard-header">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-lg-12">
                                <ul className="list-inline">
                                    <li className="list-inline-item">
                                        <Link to="/" className="navbar-brand">
                                            <img src={img_leap_logo} alt="qlector" className="logo" />
                                        </Link>
                                    </li>
                                    <li className="list-inline-item">
                                        <h2>{title}</h2>
                                    </li>
                                    <li className="list-inline-item">
                                        <ul className="navbar-nav flex-row ml-md-auto d-none d-md-flex">
                                            {range_change && <li>{time_ranges}</li>}
                                            <li className="time_range dashboard-time-range">
                                                {range_change && toISODateTimeString(ts_from)}
                                                {range_change && <span>&ndash;</span>}
                                                {toISODateTimeString(ts_to)}
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Content panels={bands} />
        </div>
    );
};
